<template>
  <div>
    <Pane />
    <a-card class="container">
      <!-- 顶部操作栏 -->
      <div class="header-wrapper">
        <a-row type="flex" justify="space-between" align="middle">
          <a-col :span="12">
            <a-button-group>
              <a-button icon="left" @click="prevMonth" />
              <a-month-picker
                v-model="selectedMonth"
                placeholder="选择年月"
                @change="onMonthChange"
                style="width: 150px"
              />
              <a-button icon="right" @click="nextMonth" />
            </a-button-group>
          </a-col>
          <a-col :span="12" style="text-align: right">
            <a-space>
              <a-button icon="reload" @click="handleRefresh" :loading="refreshing">
                刷新汇总统计
              </a-button>
              <a-button type="primary" icon="edit" @click="goToFill">
                填报计划
              </a-button>
              <a-button type="primary" icon="bar-chart" @click="goToSummary" v-if="isAdmin">
                汇总统计
              </a-button>
              <a-button icon="download" @click="handleExport" :loading="exporting">
                导出Excel
              </a-button>
            </a-space>
          </a-col>
        </a-row>
      </div>

      <Padding />

      <!-- 数据表格 -->
      <a-table
        bordered
        :data-source="list"
        :loading="loading"
        :pagination="{
          total,
          current,
          pageSize,
          showTotal: (total) => `共 ${total} 条记录`,
        }"
        @change="onChange"
        :scroll="{ x: 'max-content' }"
      >
        <!-- 部门列 -->
        <a-table-column title="部门" data-index="deptName" width="150px" fixed="left" />

        <!-- 客商名称列 -->
        <a-table-column title="客商名称" width="150px">
          <template #default="text, record">
            <span>{{ record.partyName || '--' }}</span>
          </template>
        </a-table-column>
        
        <!-- 项目名列 -->
        <a-table-column title="项目名" data-index="contractName" width="150px" />
        
        <!-- 收款计划列组 -->
        <a-table-column-group title="收款计划（元）">
          <a-table-column title="累计收款计划" width="130px" align="right">
            <template #default="text, record">
              <span style="font-weight: bold">{{ getCumulativePlanAmount(record) }}</span>
            </template>
          </a-table-column>
          <a-table-column
            v-for="week in weekColumns"
            :key="'plan-' + week.weekNumber"
            :title="`第${week.weekNumber}周`"
            width="100px"
            align="right"
          >
            <template #default="text, record">
              <span>{{ getPlanAmount(record, week.weekNumber) }}</span>
            </template>
          </a-table-column>
        </a-table-column-group>
        
        <!-- 实际回款列组 -->
        <a-table-column-group title="实际回款（元）">
          <a-table-column title="累计实际回款" width="130px" align="right">
            <template #default="text, record">
              <span style="font-weight: bold">{{ getCumulativeActualAmount(record) }}</span>
            </template>
          </a-table-column>
          <a-table-column
            v-for="week in weekColumns"
            :key="'actual-' + week.weekNumber"
            :title="`第${week.weekNumber}周`"
            width="100px"
            align="right"
          >
            <template #default="text, record">
              <span>{{ getActualAmount(record, week.weekNumber) }}</span>
            </template>
          </a-table-column>
        </a-table-column-group>
        
        <!-- 进展及计划列组 -->
        <a-table-column-group title="进展及计划">
          <a-table-column title="计划未回收" width="130px" align="right">
            <template #default="text, record">
              <span style="font-weight: bold; color: #ff4d4f">{{ getUncollectedAmount(record) }}</span>
            </template>
          </a-table-column>
          <a-table-column
            v-for="week in weekColumns"
            :key="'progress-' + week.weekNumber"
            :title="`第${week.weekNumber}周`"
            width="150px"
          >
            <template #default="text, record">
              <span>{{ getProgressPlan(record, week.weekNumber) }}</span>
            </template>
          </a-table-column>
        </a-table-column-group>
      </a-table>

      <!-- 日历表格 -->
      <div v-if="weekColumns.length > 0" style="margin-top: 16px">
        <a-card title="月度日历" :bordered="false" size="small">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px">
            <thead>
              <tr style="background: #f5f5f5">
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周一</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周二</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周三</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周四</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周五</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%; color: #999">周六</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%; color: #999">周日</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(week, weekIndex) in calendarWeeks" :key="weekIndex">
                <td 
                  v-for="(day, dayIndex) in week" 
                  :key="dayIndex"
                  :style="{
                    padding: '8px',
                    border: '1px solid #e8e8e8',
                    textAlign: 'center',
                    backgroundColor: day.isCurrentMonth ? (day.isWeekend ? '#fff9f9' : '#fff') : '#f9f9f9',
                    color: day.isCurrentMonth ? (day.isWeekend ? '#999' : '#333') : '#ccc'
                  }"
                >
                  <div v-if="day.day">
                    <div style="font-weight: bold">{{ day.day }}</div>
                    <div v-if="day.weekNumber" style="font-size: 10px; color: #1890ff">第{{ day.weekNumber }}周</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </a-card>
      </div>
    </a-card>

    <!-- 刷新汇总统计对话框 -->
    <a-modal
      v-model="refreshDialogVisible"
      title="刷新汇总统计"
      @ok="confirmRefresh"
      @cancel="cancelRefresh"
      :confirmLoading="refreshing"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="选择年月">
          <a-month-picker
            v-model="refreshYearMonth"
            placeholder="选择年月"
            style="width: 100%"
          />
        </a-form-item>
      </a-form>
      <div style="margin-top: 16px; color: #999; font-size: 12px">
        提示：刷新汇总统计会重新计算指定月份的实际回款金额，请谨慎操作。
      </div>
    </a-modal>
  </div>
</template>

<script>
import { getByYearMonth, triggerSummaryTask, summaryTask, getWeeks } from "@/api/finance/project-payment-plan";
import { fetchDetail as fetchContractDetail } from "@/api/contract";
import { downloadByAoa } from "@/utils/xlsx";
import moment from "moment";

export default {
  data() {
    return {
      selectedMonth: null,
      currentMonth: "",
      list: [],
      loading: false,
      refreshing: false,
      exporting: false,
      total: 0,
      current: 1,
      pageSize: 10,
      weekColumns: [],
      isAdmin: false,
      calendarWeeks: [],
      refreshDialogVisible: false,
      refreshYearMonth: null,
    };
  },

  computed: {
    yearMonth() {
      if (this.selectedMonth) {
        return moment(this.selectedMonth).format("YYYY-MM");
      }
      return this.currentMonth;
    },
  },

  created() {
    this.checkAdminRole();
    this.initCurrentMonth();
    this.getList();
  },

  methods: {
    // 检查是否是管理员
    checkAdminRole() {
      const userRoles = this.$store.state.auth?.user?.roles || [];
      if (Array.isArray(userRoles)) {
        const index = userRoles.findIndex(
          (item) => item.code === "finance_payment_plan_admin"
        );
        this.isAdmin = index > -1;
      } else {
        this.isAdmin = false;
      }
    },

    // 初始化当前月份（默认打开实际的当前月份）
    initCurrentMonth() {
      const now = new Date();
      // 默认打开当前月份（实际的当前月份，不是业务月）
      this.currentMonth = moment(now).format("YYYY-MM");
      this.selectedMonth = moment(this.currentMonth, "YYYY-MM");
    },

    // 获取列表数据
    getList() {
      this.loading = true;
      getByYearMonth(this.yearMonth, null, this.current, this.pageSize)
        .then((res) => {
          if (Array.isArray(res.list)) {
            this.list = res.list;
            this.total = res.totalSize;
            this.generateWeekColumns();
            // 根据合同信息补全客商名称
            this.loadPartyNames();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 根据合同ID查询合同信息，补全客商名称（partyAname）
    loadPartyNames() {
      const tasks = this.list.map((record) => {
        if (!record.contractId) return Promise.resolve();
        return fetchContractDetail(record.contractId)
          .then((contract) => {
            const partyName = (contract && (contract["partyAname"] || contract["partyaname"])) || "";
            this.$set(record, "partyName", partyName);
          })
          .catch(() => {
            this.$set(record, "partyName", "");
          });
      });
      return Promise.all(tasks);
    },

    // 导出Excel（查询当月全部数据，pageSize=1000）
    handleExport() {
      this.exporting = true;
      getByYearMonth(this.yearMonth, null, 1, 1000)
        .then((res) => {
          const records = Array.isArray(res.list) ? res.list : [];
          if (!records.length) {
            this.$message.warning("暂无数据可导出");
            return;
          }
          // 补全客商名称
          return Promise.all(
            records.map((record) => {
              if (!record.contractId) {
                record.partyName = "";
                return Promise.resolve();
              }
              return fetchContractDetail(record.contractId)
                .then((contract) => {
                  record.partyName =
                    (contract && (contract["partyAname"] || contract["partyaname"])) || "";
                })
                .catch(() => {
                  record.partyName = "";
                });
            })
          ).then(() => {
            const weeks = this.weekColumns;
            const w = weeks.length;

            // 第一行：分组表头（与页面表格的列组一致）
            const header1 = ["部门", "客商名称", "项目名", "收款计划（元）"];
            for (let i = 0; i < w; i++) header1.push("");
            header1.push("实际回款（元）");
            for (let i = 0; i < w; i++) header1.push("");
            header1.push("进展及计划");
            for (let i = 0; i < w; i++) header1.push("");

            // 第二行：各列名称
            const header2 = ["", "", "", "累计收款计划"];
            weeks.forEach((week) => header2.push(`第${week.weekNumber}周`));
            header2.push("累计实际回款");
            weeks.forEach((week) => header2.push(`第${week.weekNumber}周`));
            header2.push("计划未回收");
            weeks.forEach((week) => header2.push(`第${week.weekNumber}周`));

            const data = [header1, header2];
            records.forEach((record) => {
              const row = [
                record.deptName || "",
                record.partyName || "--",
                record.contractName || "",
                this.getCumulativePlanAmount(record),
              ];
              weeks.forEach((week) => row.push(this.getPlanAmount(record, week.weekNumber)));
              row.push(this.getCumulativeActualAmount(record));
              weeks.forEach((week) => row.push(this.getActualAmount(record, week.weekNumber)));
              row.push(this.getUncollectedAmount(record));
              weeks.forEach((week) => row.push(this.getProgressPlan(record, week.weekNumber)));
              data.push(row);
            });

            // 合并单元格：前三列纵向合并，三个分组表头横向合并
            const merges = [
              { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
              { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
              { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
            ];
            if (w > 0) {
              merges.push({ s: { r: 0, c: 3 }, e: { r: 0, c: 3 + w } });
              merges.push({ s: { r: 0, c: 4 + w }, e: { r: 0, c: 4 + 2 * w } });
              merges.push({ s: { r: 0, c: 5 + 2 * w }, e: { r: 0, c: 5 + 3 * w } });
            }

            // 列宽
            const cols = [{ wch: 15 }, { wch: 20 }, { wch: 25 }];
            for (let g = 0; g < 3; g++) {
              cols.push({ wch: 14 });
              for (let i = 0; i < w; i++) cols.push({ wch: g === 2 ? 30 : 12 });
            }

            downloadByAoa(data, `${this.yearMonth}项目回款计划`, merges, cols);
          });
        })
        .catch((err) => {
          this.$message.error("导出失败：" + (err.message || "未知错误"));
        })
        .finally(() => {
          this.exporting = false;
        });
    },

    // 手动触发定时任务刷新数据
    // 刷新汇总统计（显示对话框）
    handleRefresh() {
      // 默认选择当前月
      this.refreshYearMonth = moment(this.currentMonth, "YYYY-MM");
      this.refreshDialogVisible = true;
    },

    // 确认刷新
    confirmRefresh() {
      if (!this.refreshYearMonth) {
        this.$message.warning("请选择年月");
        return;
      }

      const yearMonth = moment(this.refreshYearMonth).format("YYYY-MM");
      this.refreshing = true;
      summaryTask(yearMonth)
        .then(() => {
          this.$message.success("刷新汇总统计成功");
          this.refreshDialogVisible = false;
          // 刷新当前列表
          this.getList();
        })
        .catch((err) => {
          this.$message.error("刷新失败：" + (err.message || "未知错误"));
        })
        .finally(() => {
          this.refreshing = false;
        });
    },

    // 取消刷新
    cancelRefresh() {
      this.refreshDialogVisible = false;
      this.refreshYearMonth = null;
    },

    // 生成周列（调用后端接口获取周信息）
    generateWeekColumns() {
      getWeeks(this.yearMonth)
        .then((res) => {
          if (res && Array.isArray(res)) {
            this.weekColumns = res.map((week) => ({
              weekNumber: week.weekNumber,
              title: `第${week.weekNumber}周`,
              dateRange: week.dateRange,
              startDate: week.startDate,
              endDate: week.endDate,
            }));
            // 生成日历表格
            this.generateCalendarWeeks();
          }
        })
        .catch((err) => {
          this.$message.error("获取周信息失败：" + (err.message || "未知错误"));
        });
    },

    // 年月选择变化
    onMonthChange(date) {
      this.selectedMonth = date;
      this.current = 1;
      this.getList();
    },

    // 分页变化
    onChange(pagination) {
      this.current = pagination.current;
      this.pageSize = pagination.pageSize;
      this.getList();
    },

    // 获取计划金额
    getPlanAmount(record, weekNumber) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        return week ? week.planAmount || 0 : 0;
      }
      return 0;
    },

    // 获取实际金额
    getActualAmount(record, weekNumber) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        return week ? week.actualAmount || 0 : 0;
      }
      return 0;
    },

    // 获取进展计划
    getProgressPlan(record, weekNumber) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find((w) => w.weekNumber === weekNumber);
        return week ? week.progressPlan || "" : "";
      }
      return "";
    },

    // 四舍五入保留两位小数
    round2(num) {
      return Math.round((num + Number.EPSILON) * 100) / 100;
    },

    // 获取累计收款计划（所有周的收款计划之和）
    getCumulativePlanAmount(record) {
      if (record.paymentPlanList) {
        return this.round2(
          record.paymentPlanList.reduce((sum, week) => sum + (Number(week.planAmount) || 0), 0)
        );
      }
      return 0;
    },

    // 获取累计实际回款（所有周的实际回款之和）
    getCumulativeActualAmount(record) {
      if (record.paymentPlanList) {
        return this.round2(
          record.paymentPlanList.reduce((sum, week) => sum + (Number(week.actualAmount) || 0), 0)
        );
      }
      return 0;
    },

    // 获取计划未回收（累计收款计划 - 累计实际回款）
    getUncollectedAmount(record) {
      return this.round2(this.getCumulativePlanAmount(record) - this.getCumulativeActualAmount(record));
    },

    // 跳转到填报页面
    goToFill() {
      this.$router.push({
        path: "/finance/project-payment-plan/fill",
        query: {
          yearMonth: this.yearMonth,
        },
      });
    },

    // 跳转到汇总页面
    goToSummary() {
      this.$router.push({
        path: "/finance/project-payment-plan/summary",
        query: {
          year: this.yearMonth.substring(0, 4),
        },
      });
    },

    // 生成日历表格
    generateCalendarWeeks() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      
      // 获取这个月的第一天是周几（0=周日, 1=周一, ..., 6=周六）
      const firstDayOfWeek = firstDay.getDay();
      
      // 计算日历的第一天（可能是上个月的最后几天）
      const startDay = new Date(firstDay);
      // 如果第一天不是周一，需要向前调整到周一
      if (firstDayOfWeek !== 1) {
        const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
        startDay.setDate(startDay.getDate() - daysToSubtract);
      }
      
      // 计算日历的最后一天（可能是下个月的最初几天）
      const endDay = new Date(lastDay);
      // 如果最后一天不是周日，需要向后调整到周日
      const lastDayOfWeek = lastDay.getDay();
      if (lastDayOfWeek !== 0) {
        const daysToAdd = 7 - lastDayOfWeek;
        endDay.setDate(endDay.getDate() + daysToAdd);
      }
      
      // 生成日历周数组
      const weeks = [];
      let currentDay = new Date(startDay);
      
      while (currentDay <= endDay) {
        const week = [];
        for (let i = 0; i < 7; i++) {
          const dayOfMonth = currentDay.getDate();
          const isCurrentMonth = currentDay.getMonth() === month - 1;
          const dayOfWeek = currentDay.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          // 判断这一天属于哪一周
          let weekNumber = null;
          if (isCurrentMonth) {
            // 查找weekColumns中对应的周
            for (const weekInfo of this.weekColumns) {
              const startDate = new Date(weekInfo.startDate);
              const endDate = new Date(weekInfo.endDate);
              if (currentDay >= startDate && currentDay <= endDate) {
                weekNumber = weekInfo.weekNumber;
                break;
              }
            }
          }
          
          week.push({
            day: dayOfMonth,
            isCurrentMonth: isCurrentMonth,
            isWeekend: isWeekend,
            weekNumber: weekNumber,
          });
          
          currentDay.setDate(currentDay.getDate() + 1);
        }
        weeks.push(week);
      }
      
      this.calendarWeeks = weeks;
    },

    // 上个月
    prevMonth() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const prevMonthDate = new Date(year, month - 2, 1); // month-2是因为month是1-12，需要减1才能得到正确的索引
      this.selectedMonth = moment(prevMonthDate);
      this.current = 1;
      this.getList();
    },

    // 下个月
    nextMonth() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const nextMonthDate = new Date(year, month, 1); // month是因为month是1-12，不需要减1
      this.selectedMonth = moment(nextMonthDate);
      this.current = 1;
      this.getList();
    },
  },
};
</script>

<style scoped>
.container {
  padding: 16px;
}

.header-wrapper {
  padding: 16px 0;
}
</style>